import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { SelectedAppointmentTimeEnum } from "@frontend/lib/stores/slices/bookingSlice";
import {
  APIProvider,
  AdvancedMarker,
  Map as GoogleMap,
  Pin,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { format } from "date-fns";
import { useEffect, useState } from "react";

const API_KEY = process.env.NEXT_PUBLIC_GCP_MAPS_API as string;

interface SingleLocationMapProps {
  address: string;
  primaryColor?: string;
  secondaryColor?: string;
}

function SingleLocationMap({
  address,
  primaryColor,
  secondaryColor,
}: SingleLocationMapProps) {
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const geocodingLib = useMapsLibrary("geocoding");

  useEffect(() => {
    if (!geocodingLib || !address) return;

    const geocoder = new geocodingLib.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const location = results[0].geometry.location;
        setCoordinates({
          lat: location.lat(),
          lng: location.lng(),
        });
      }
    });
  }, [geocodingLib, address]);

  if (!coordinates) {
    return (
      <div className="w-full h-full bg-gray-100 rounded-xl flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading map...</p>
      </div>
    );
  }

  return (
    <GoogleMap
      className="w-full h-full rounded-xl"
      defaultZoom={14}
      defaultCenter={coordinates}
      center={coordinates}
      disableDefaultUI={true}
      mapId={"4fdb380eed181cb7"}
      gestureHandling={"greedy"}
    >
      <AdvancedMarker position={coordinates}>
        <Pin
          background={secondaryColor ?? "#01A0B5"}
          borderColor={secondaryColor ?? "#006425"}
          glyphColor={"#ffffff"}
        />
      </AdvancedMarker>
    </GoogleMap>
  );
}

export const BookingDateTimeCard = ({
  logoUrl,
  showMap = false,
  practiceName,
  address,
  phoneNumber,
  primaryColor,
  secondaryColor,
}: {
  logoUrl?: string | null;
  showMap: boolean;
  practiceName: string;
  address: string;
  phoneNumber: string;
  primaryColor: string;
  secondaryColor: string;
}) => {
  const t = useTranslations("Shop");
  const selectedAppointmentTime = useBoundStore.use.selectedAppointmentTime();
  let date: Date | null = null;
  let formattedTime: string | null = null;

  if (selectedAppointmentTime instanceof Date) {
    date = selectedAppointmentTime;
    formattedTime = format(selectedAppointmentTime, "h:mm a");
  } else if (selectedAppointmentTime === SelectedAppointmentTimeEnum.ASAP) {
    date = new Date();
    formattedTime = "ASAP";
  }

  return (
    <>
      <div
        className="w-full h-fit flex flex-col gap-4 sm:justify-between p-6 bg-white border border-[#E1E1E1] rounded-2xl "
        style={{
          borderRadius: showMap ? "16px 16px 0 0" : "16px",
        }}
      >
        {date && formattedTime && (
          <p className="font-medium text-md text-black">
            {format(date, "MM/dd/yyyy")} {t("At")} {formattedTime}
          </p>
        )}
        <div className="flex flex-col">
          <p
            className="font-medium text-lg"
            style={{ color: primaryColor ?? "#000000" }}
          >
            {practiceName}
          </p>
          <p className="text-sm text-gray-500">{address}</p>
          <p className="text-sm text-gray-500">{phoneNumber}</p>
        </div>
      </div>
      {showMap && address && (
        <div
          className=" h-96"
          style={{ borderRadius: showMap ? "0 0 16px 16px" : "16px" }}
        >
          <APIProvider apiKey={API_KEY} version="quarterly">
            <SingleLocationMap
              address={address}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            />
          </APIProvider>
        </div>
      )}
    </>
  );
};

export default BookingDateTimeCard;
